/* ===========================================================================
   Add-to-cart upsell modal.

   Opens instead of adding, offers a second bottle that crosses the free-shipping
   threshold, and — when a discount code is configured — applies that code to the
   session so the customer never has to type it.

   The discount itself lives in Shopify. This only applies the code; if the code
   is wrong, expired, or its conditions are not met, Shopify simply will not
   discount the order. That is why the modal only advertises a discount when a
   code has actually been configured.
   =========================================================================== */
(() => {
  if (customElements.get('dbp-upsell')) return;

  class DbpUpsell extends HTMLElement {
    connectedCallback() {
      // The modal is rendered inside main-product's info column, which Dawn styles
      // `position: sticky; z-index: 2`. That creates a stacking context, and a
      // position:fixed child cannot escape it: the panel paints *under* the product
      // gallery, and backdrop-filter only blurs what is inside that same context —
      // so the page behind it stays sharp. Portal to <body> to get a clean context.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this); // re-fires connectedCallback with body as parent
        return;
      }
      if (this.initialised) return;
      this.initialised = true;

      this.panel = this.querySelector('.dbp-upsell__panel');
      this.errorEl = this.querySelector('[data-upsell-error]');
      this.baseVariant = Number(this.dataset.baseVariant);
      this.code = this.dataset.code || '';

      // X / backdrop / Escape must NOT throw away the add. The shopper already
      // clicked Add to cart; dismissing the upsell means "just this one", not
      // "cancel my purchase". Only take() may hide the modal without adding.
      this.querySelectorAll('[data-upsell-close]').forEach((el) =>
        el.addEventListener('click', () => this.dismiss())
      );
      this.querySelectorAll('[data-upsell-take]').forEach((btn) =>
        btn.addEventListener('click', () => this.take(btn))
      );
      this.querySelector('[data-upsell-decline]')?.addEventListener('click', () => this.decline());

      this.onKey = (e) => {
        if (e.key === 'Escape') this.dismiss();
        if (e.key === 'Tab') this.trap(e);
      };

      window.dbpUpsell = this;
    }

    /* ---------- open / close ---------- */

    open(form, addResponse, baseQty) {
      this.form = form;
      // The product is already in the cart by the time this opens. These two
      // carry what the modal needs to finish the job without adding it twice:
      // the sections from that add (so dismissing can render the drawer) and
      // how many units are already there (so "take" adds only the difference).
      this.addResponse = addResponse || null;
      this.baseQty = Number(baseQty) || 1;
      this.lastFocus = document.activeElement;
      this.hideError();
      this.hidden = false;
      document.body.style.overflow = 'hidden';
      // keep the page behind a aria-modal dialog out of the tab order entirely
      this.inerted = [...document.body.children].filter((el) => el !== this && !el.hasAttribute('inert'));
      this.inerted.forEach((el) => el.setAttribute('inert', ''));
      document.addEventListener('keydown', this.onKey);
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        (this.querySelector('[data-upsell-take]') || this.querySelector('[data-upsell-decline]'))?.focus();
      });
    }

    /** Dismissed without choosing a companion. The product is already in the cart. */
    dismiss() {
      this.decline();
    }

    /** Hide only. Used after a bundle has already been added. */
    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      (this.inerted || []).forEach((el) => el.removeAttribute('inert'));
      this.inerted = [];
      document.removeEventListener('keydown', this.onKey);
      setTimeout(() => { this.hidden = true; }, 180);
      this.lastFocus?.focus?.();
    }

    trap(e) {
      const items = [...this.panel.querySelectorAll('button:not([disabled])')];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      // focus escaped the dialog entirely (e.g. blurred to <body>) — pull it back
      if (!this.panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- actions ---------- */

    decline() {
      // Nothing to undo and nothing to re-send: the add already happened before
      // this modal was ever shown. Just close and show them their cart.
      this.close();
      this.showCart();
    }

    /** Opens the drawer with the sections captured from the add that preceded us. */
    showCart() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') return;
      drawer.classList.remove('is-empty');
      if (this.addResponse && this.addResponse.sections) drawer.renderContents(this.addResponse);
      else if (typeof drawer.open === 'function') drawer.open();
    }

    async take(btn) {
      const variant = Number(btn.dataset.variantId);
      if (!variant) return;
      const qty = Number(btn.dataset.qty) || 1;
      const isSelf = btn.dataset.self === '1';

      // Disabling the button the user just activated blurs focus to <body>, which
      // escapes the dialog. Use aria-disabled plus a guard flag instead.
      if (this.busy) return;
      this.busy = true;
      const label = btn.textContent;
      btn.setAttribute('aria-disabled', 'true');
      btn.textContent = 'Adding…';
      this.hideError();

      try {
        // Apply the discount code first so the cart reflects it immediately.
        if (this.code) {
          try {
            await fetch(`/discount/${encodeURIComponent(this.code)}`, { credentials: 'same-origin' });
          } catch (_) {
            /* a failed code must not block the add — Shopify just will not discount */
          }
        }

        const drawer = document.querySelector('cart-drawer');
        const sections = drawer ? drawer.getSectionsToRender().map((s) => s.id) : [];
        // The base product is already in the cart, so only the difference is
        // added here. "Two of this bottle" when one is already in means adding
        // one, not two; a companion offer means adding the companion alone.
        const topUp = isSelf ? qty - this.baseQty : 0;
        const items = isSelf
          ? topUp > 0
            ? [{ id: this.baseVariant, quantity: topUp }]
            : []
          : [{ id: variant, quantity: qty }];

        if (!items.length) {
          // They already have as many as the offer would give them.
          this.close();
          this.showCart();
          return;
        }

        const body = { items };
        if (sections.length) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }

        const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        });
        const data = await res.json();

        if (!res.ok || data.status) {
          this.showError(data.description || data.message || 'These items could not be added to your cart.');
          return;
        }

        this.close();
        if (drawer && data.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      } catch (_) {
        this.showError('Connection problem. Please try again.');
      } finally {
        this.busy = false;
        btn.removeAttribute('aria-disabled');
        btn.textContent = label;
      }
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.classList.add('is-shown');
      // set the text last: the node is already in the tree, so this mutation is
      // what the live region actually announces
      this.errorEl.textContent = msg;
      this.errorEl.setAttribute('tabindex', '-1');
      this.errorEl.focus?.();
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.classList.remove('is-shown');
      this.errorEl.textContent = '';
    }
  }

  customElements.define('dbp-upsell', DbpUpsell);
})();
